import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import * as Yup from 'yup';
import TextareaAutosize from 'legacy/common/components/wysiwyg-form/TextareaAutoSize';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { MdLink } from 'react-icons/md';
import LinkForm from './LinkForm';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import useWaiverChangeHandler from 'legacy/events/hooks/useWaiverChangeHandler';

export interface Waiver {
  content: string;
  title: string;
  id: string;
}

export type LinkFormSubmitionType = {
  text: string;
  link: string;
  values: { waivers: Waiver[] };
  setFieldValue: (field: string, value: Waiver[]) => void;
};
interface WaiverModalProps {
  initialValues: { waivers: Waiver[] };
  onClose: () => void;
  eventId: string | undefined;
  refetchEvent: () => void;
}

const CHARACTERS_LIMIT = 5000;

const WaiverModal: React.FC<WaiverModalProps> = ({
  initialValues,
  onClose,
  eventId,
  refetchEvent
}) => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [activeWaiverIndex, setActiveWaiverIndex] = useState<number | null>(
    null
  );
  const {
    handleWaiversChange,
    createEventWaiverLoading,
    updateEventWaiverLoading,
    deleteEventWaiverLoading
  } = useWaiverChangeHandler(eventId);
  const toggleModal = () => {
    setIsLinkModalOpen(!isLinkModalOpen);
  };
  const handleRightIconClick = (index: number) => {
    toggleModal();
    setActiveWaiverIndex(index);
  };
  const handleLinkFormSubmit = ({
    text,
    link,
    values,
    setFieldValue
  }: LinkFormSubmitionType) => {
    if (activeWaiverIndex !== null && text.trim() !== '') {
      const newWaivers = [...values.waivers];

      newWaivers[activeWaiverIndex].content += `\n[${text}](${link})`;

      setFieldValue('waivers', newWaivers);
    }
    toggleModal();
  };

  const removeWaiver = (
    index: number,
    waivers: Waiver[],
    setFieldValue: (field: string, value: Waiver[]) => void
  ) => {
    const newWaivers = [...waivers];
    newWaivers.splice(index, 1);
    if (newWaivers.length === 0) {
      newWaivers.push({
        title: '',
        content: '',
        id: ''
      });
    }
    setFieldValue('waivers', newWaivers);
  };

  const addNewWaiver = (
    waivers: Waiver[],
    setFieldValue: (field: string, value: Waiver[]) => void
  ) => {
    setFieldValue('waivers', [...waivers, { title: '', content: '', id: '' }]);
  };

  const handleOnSubmit = async (values: { waivers: Waiver[] }) => {
    await handleWaiversChange(initialValues.waivers, values.waivers);
    refetchEvent();
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={Yup.object().shape({
        waivers: Yup.array().of(
          Yup.object().shape(
            {
              title: Yup.string().when('content', {
                is: (content: string) => content && content.trim() !== '',
                then: Yup.string().required('Title is required'),
                otherwise: Yup.string()
              }),
              content: Yup.string()
                .when('title', {
                  is: (title: string) => title && title.trim() !== '',
                  then: Yup.string().required('Content is required'),
                  otherwise: Yup.string()
                })
                .max(
                  CHARACTERS_LIMIT,
                  `Content cannot exceed ${CHARACTERS_LIMIT} characters`
                )
            },
            [['title', 'content']]
          )
        )
      })}
      enableReinitialize
    >
      {({ values, submitForm, setFieldValue, errors }) => (
        <Modal
          type={ModalType.SMALL}
          onClose={onClose}
          visible
          isBehind={isLinkModalOpen}
          icon={<img src="/img/events/create/waiver.svg" className="w-6 h-6" />}
          title="Add event waivers"
          renderFooter={() => (
            <Button
              disabled={errors?.waivers?.length > 0}
              loading={
                createEventWaiverLoading ||
                updateEventWaiverLoading ||
                deleteEventWaiverLoading
              }
              type={ButtonType.Primary}
              onClick={submitForm}
            >
              Save Waiver
            </Button>
          )}
        >
          <div className="px-4 pt-6 pb-12">
            <p className="text-sm text-gray-500 mb-2 font-normal">
              Enter the contents of your waiver. Guests need to accept this
              before purchasing or claiming a ticket. We suggest hyperlinking to
              longer waivers.
            </p>
            {values.waivers.map((waiver, index) => (
              <FadeIn key={waiver.id}>
                <div className="mt-4" key={waiver.id}>
                  <Field
                    label="Name"
                    id={`waiver-title-${index}`}
                    name={`waivers[${index}].title`}
                    clearable
                    placeholder="e.g. Code of Conduct"
                  />

                  <TextareaAutosize
                    label="Waiver text"
                    id={`waiver-content-${index}`}
                    name={`waivers[${index}].content`}
                    placeholder="Add waiver text here..."
                    RightIcon={() => <MdLink size={20} />}
                    handleRightIconClick={() => handleRightIconClick(index)}
                    error={(errors?.waivers?.[index] as Waiver)?.content}
                  />

                  <div className="mt-2  flex flex-row justify-between items-center">
                    <div className="border-b-1 border-gray-200 w-4/5" />
                    <div
                      className="cursor-pointer text-red-600 text-sm font-medium"
                      onClick={() =>
                        removeWaiver(index, values.waivers, setFieldValue)
                      }
                    >
                      Remove
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
            <button
              type="button"
              className="mt-4 text-blue-600 font-normal font-sfprotext text-base leading-5 tracking-tight"
              onClick={() => addNewWaiver(values.waivers, setFieldValue)}
            >
              + Add another waiver
            </button>
          </div>
          {isLinkModalOpen && (
            <LinkForm
              onClose={toggleModal}
              onSubmit={({ text, link }) =>
                handleLinkFormSubmit({ text, link, values, setFieldValue })
              }
            />
          )}
        </Modal>
      )}
    </Formik>
  );
};

export default WaiverModal;
